import { normalizeOib } from '@/modules/point-of-sale/components/warehouse/warehouse-module/einvoice/oib'

/**
 * UBL XML Parser for eInvoice/eRacun documents
 * Parses UBL 2.1 format XML invoices and extracts invoice data
 * Uses native browser DOMParser - no external dependencies
 */

class UblXmlParser {
  constructor (xmlString) {
    this.parser = new DOMParser()
    this.xmlDoc = this.parser.parseFromString(xmlString, 'text/xml')

    // Check for parsing errors
    const parserError = this.xmlDoc.querySelector('parsererror')
    if (parserError) {
      throw new Error('XML parsing error: ' + parserError.textContent)
    }

    // Common UBL namespaces
    this.namespaces = {
      cbc: 'urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2',
      cac: 'urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2',
      ubl: 'urn:oasis:names:specification:ubl:schema:xsd:Invoice-2'
    }
  }

  /**
   * Parse invoice header information
   * @returns {Object} Invoice header data
   */
  parseInvoiceHeader () {
    try {
      const root = this.xmlDoc.documentElement
      const rootText = tag => this.getTextContent(this.getChildren(root, tag, 'cbc')[0] || null)
      const invoiceId = rootText('ID') || ''
      const issueDate = rootText('IssueDate') || null
      const currency = rootText('DocumentCurrencyCode') || 'EUR'

      // Parse supplier party information
      const supplierParty = this.getElement(this.xmlDoc, 'AccountingSupplierParty', 'cac')
      let supplierName = ''
      let supplierOib = ''

      if (supplierParty) {
        const party = this.getElement(supplierParty, 'Party', 'cac')
        if (party) {
          // Get supplier name
          const partyName = this.getElement(party, 'PartyName', 'cac')
          if (partyName) {
            supplierName = this.getElementText(partyName, 'Name', 'cbc') || ''
          }

          // Try to get from PartyLegalEntity if not in PartyName
          if (!supplierName) {
            const legalEntity = this.getElement(party, 'PartyLegalEntity', 'cac')
            if (legalEntity) {
              supplierName = this.getElementText(legalEntity, 'RegistrationName', 'cbc') || ''
            }
          }

          // Get supplier OIB (tax ID)
          const partyTaxScheme = this.getElement(party, 'PartyTaxScheme', 'cac')
          if (partyTaxScheme) {
            supplierOib = this.getElementText(partyTaxScheme, 'CompanyID', 'cbc') || ''
          }

          // Alternative: try PartyIdentification
          if (!supplierOib) {
            const partyId = this.getElement(party, 'PartyIdentification', 'cac')
            if (partyId) {
              supplierOib = this.getElementText(partyId, 'ID', 'cbc') || ''
            }
          }
        }
      }

      // Parse monetary totals
      const legalMonetaryTotal = this.getElement(this.xmlDoc, 'LegalMonetaryTotal', 'cac')
      let taxExclusiveAmount = 0
      let taxInclusiveAmount = 0

      if (legalMonetaryTotal) {
        taxExclusiveAmount = parseFloat(
          this.getElementText(legalMonetaryTotal, 'TaxExclusiveAmount', 'cbc') || '0'
        )
        taxInclusiveAmount = parseFloat(
          this.getElementText(legalMonetaryTotal, 'TaxInclusiveAmount', 'cbc') || '0'
        )
      }

      const documentCharges = this.getChildren(root, 'AllowanceCharge', 'cac').map(ac => ({
        charge: this.getElementText(ac, 'ChargeIndicator', 'cbc') === 'true',
        reason: this.getElementText(ac, 'AllowanceChargeReason', 'cbc'),
        amount: parseFloat(this.getElementText(ac, 'Amount', 'cbc') || '0'),
        taxCategoryName: this.getElementText(this.getElement(ac, 'TaxCategory', 'cac'), 'Name', 'cbc')
      }))

      return {
        invoiceId,
        issueDate,
        supplierName,
        supplierOib: normalizeOib(supplierOib),
        currency,
        taxExclusiveAmount,
        taxInclusiveAmount,
        totalAmount: taxInclusiveAmount,
        documentCharges
      }
    } catch (error) {
      console.error('Error parsing invoice header:', error)
      return {
        invoiceId: '',
        issueDate: null,
        supplierName: '',
        supplierOib: '',
        currency: 'EUR',
        taxExclusiveAmount: 0,
        taxInclusiveAmount: 0,
        totalAmount: 0,
        documentCharges: []
      }
    }
  }

  /**
   * Parse invoice line items
   * @returns {Array} Array of line items
   */
  parseInvoiceLines () {
    try {
      const invoiceLines = this.getElements(this.xmlDoc, 'InvoiceLine', 'cac')
      const items = []

      invoiceLines.forEach((line, index) => {
        try {
          const item = this.parseInvoiceLine(line, index)
          if (item) {
            items.push(item)
          }
        } catch (error) {
          console.error(`Error parsing line ${index}:`, error)
          // Continue with other lines even if one fails
        }
      })

      return items
    } catch (error) {
      console.error('Error parsing invoice lines:', error)
      return []
    }
  }

  /**
   * Parse a single invoice line
   * @param {Element} line - InvoiceLine XML element
   * @param {Number} index - Line index
   * @returns {Object} Parsed line item
   */
  parseInvoiceLine (line, index) {
    // Get line ID
    const lineId = this.getElementText(line, 'ID', 'cbc') || (index + 1).toString()

    // Get quantity
    const quantityElement = this.getElement(line, 'InvoicedQuantity', 'cbc')
    const quantity = parseFloat(this.getTextContent(quantityElement) || '1')
    const unitCode = quantityElement ? quantityElement.getAttribute('unitCode') || 'kom' : 'kom'

    // Get line extension amount (total before tax)
    const lineExtensionAmount = parseFloat(
      this.getElementText(line, 'LineExtensionAmount', 'cbc') || '0'
    )

    // Get item information
    const itemElement = this.getElement(line, 'Item', 'cac')
    let itemName = ''
    let sellersItemId = ''
    const additionalItemIds = []
    const standardItemIds = []

    if (itemElement) {
      const sellersIdentification = this.getChildren(itemElement, 'SellersItemIdentification', 'cac')[0]
      if (sellersIdentification) {
        sellersItemId = this.getTextContent(this.getChildren(sellersIdentification, 'ID', 'cbc')[0])
      }

      this.getChildren(itemElement, 'AdditionalItemIdentification', 'cac').forEach(identification => {
        const id = this.getTextContent(this.getChildren(identification, 'ID', 'cbc')[0])
        if (id) additionalItemIds.push(id)
      })

      this.getChildren(itemElement, 'StandardItemIdentification', 'cac').forEach(identification => {
        const idElement = this.getChildren(identification, 'ID', 'cbc')[0]
        const id = this.getTextContent(idElement)
        if (id) standardItemIds.push({ id, schemeId: idElement.getAttribute('schemeID') || '' })
      })

      itemName = this.getElementText(itemElement, 'Name', 'cbc') || ''

      // Try Description if Name is empty
      if (!itemName) {
        itemName = this.getElementText(itemElement, 'Description', 'cbc') || ''
      }
    }

    // Get price
    const priceElement = this.getElement(line, 'Price', 'cac')
    let unitPrice = 0

    if (priceElement) {
      const priceAmount = parseFloat(
        this.getElementText(priceElement, 'PriceAmount', 'cbc') || '0'
      )
      const baseQuantity = parseFloat(this.getElementText(priceElement, 'BaseQuantity', 'cbc'))
      unitPrice = priceAmount / (baseQuantity > 0 ? baseQuantity : 1)
    } else if (quantity > 0) {
      // Calculate from line total if price not provided
      unitPrice = lineExtensionAmount / quantity
    }

    // Get tax information
    let taxRate = 0
    const taxTotal = this.getElement(line, 'TaxTotal', 'cac')

    if (taxTotal) {
      const taxSubtotal = this.getElement(taxTotal, 'TaxSubtotal', 'cac')
      if (taxSubtotal) {
        const taxCategory = this.getElement(taxSubtotal, 'TaxCategory', 'cac')
        if (taxCategory) {
          taxRate = parseFloat(
            this.getElementText(taxCategory, 'Percent', 'cbc') || '0'
          )
        }
      }
    }

    // If no tax info on line level, try to get from Item
    if (taxRate === 0 && itemElement) {
      const classifiedTaxCategory = this.getElement(itemElement, 'ClassifiedTaxCategory', 'cac')
      if (classifiedTaxCategory) {
        taxRate = parseFloat(
          this.getElementText(classifiedTaxCategory, 'Percent', 'cbc') || '0'
        )
      }
    }

    const itemTaxCategory = itemElement && this.getElement(itemElement, 'ClassifiedTaxCategory', 'cac')
    const taxCategoryName = (itemTaxCategory && this.getElementText(itemTaxCategory, 'Name', 'cbc')) || ''

    // Adjust tax rate to nearest standard Croatian rate
    taxRate = this.normalizeVatRate(taxRate)

    // Get discounts and allowances
    let discount = 0
    let depositFee = 0
    let chargesTotal = 0 // all line charges, deposit included

    const allowanceCharges = this.getChildren(line, 'AllowanceCharge', 'cac')
    allowanceCharges.forEach(ac => {
      const chargeIndicator = this.getElementText(ac, 'ChargeIndicator', 'cbc')
      const amount = parseFloat(this.getElementText(ac, 'Amount', 'cbc') || '0')
      const taxCategory = this.getElement(ac, 'TaxCategory', 'cac')
      const isDeposit = this.getElementText(taxCategory, 'Name', 'cbc') === 'HR:POVNAK'

      if (chargeIndicator === 'false') {
        discount += amount
      } else if (chargeIndicator === 'true') {
        chargesTotal += amount
        if (isDeposit) depositFee += amount
      }
    })

    // Some suppliers send a PriceAmount that is already net of allowances; restore the gross price
    const gap = value => Math.abs(value - lineExtensionAmount)
    if (discount > 0 && quantity > 0 &&
        gap(quantity * unitPrice - discount + chargesTotal) > 0.02 &&
        gap(quantity * unitPrice) <= 0.02) {
      unitPrice = (lineExtensionAmount + discount - chargesTotal) / quantity
    }

    // Calculate price with tax
    const priceWithTax = unitPrice * (1 + taxRate / 100)

    return {
      lineId,
      name: itemName || `Artikl ${lineId}`,
      quantity,
      unitCode,
      unitPrice,
      priceWithTax,
      taxRate,
      lineTotal: lineExtensionAmount,
      discount,
      depositFee,
      chargesTotal: chargesTotal - depositFee,
      taxCategoryName,
      sellersItemId,
      additionalItemIds,
      standardItemIds
    }
  }

  /**
   * Normalize VAT rate to standard Croatian rates
   * @param {Number} rate - Raw VAT rate
   * @returns {Number} Normalized rate (0, 5, 13, or 25)
   */
  normalizeVatRate (rate) {
    const standardRates = [0, 5, 13, 25]

    // Find closest standard rate
    let closest = standardRates[0]
    let minDiff = Math.abs(rate - closest)

    for (let i = 1; i < standardRates.length; i++) {
      const diff = Math.abs(rate - standardRates[i])
      if (diff < minDiff) {
        minDiff = diff
        closest = standardRates[i]
      }
    }

    return closest
  }

  /**
   * Get single element by tag name with optional namespace
   * @param {Element} parent - Parent element
   * @param {String} tagName - Tag name to search for
   * @param {String} namespace - Namespace prefix (cbc, cac, ubl)
   * @returns {Element|null}
   */
  getElement (parent, tagName, namespace = null) {
    if (!parent) return null

    if (namespace && this.namespaces[namespace]) {
      const elements = parent.getElementsByTagNameNS(this.namespaces[namespace], tagName)
      return elements.length > 0 ? elements[0] : null
    }

    // Fallback to non-namespaced search
    const elements = parent.getElementsByTagName(tagName)
    if (elements.length > 0) return elements[0]

    // Try with namespace prefix
    if (namespace) {
      const prefixedElements = parent.getElementsByTagName(`${namespace}:${tagName}`)
      return prefixedElements.length > 0 ? prefixedElements[0] : null
    }

    return null
  }

  /**
   * Get direct child elements by tag name and namespace
   * @param {Element} parent - Parent element
   * @param {String} tagName - Local tag name
   * @param {String} namespace - Namespace prefix (cbc, cac, ubl)
   * @returns {Array} Array of direct child elements
   */
  getChildren (parent, tagName, namespace) {
    if (!parent) return []
    const nsUri = this.namespaces[namespace]
    return Array.from(parent.childNodes).filter(node =>
      node.nodeType === 1 && node.localName === tagName && node.namespaceURI === nsUri
    )
  }

  /**
   * Get multiple elements by tag name with optional namespace
   * @param {Element} parent - Parent element
   * @param {String} tagName - Tag name to search for
   * @param {String} namespace - Namespace prefix
   * @returns {Array} Array of elements
   */
  getElements (parent, tagName, namespace = null) {
    if (!parent) return []

    let elements = []

    if (namespace && this.namespaces[namespace]) {
      elements = Array.from(
        parent.getElementsByTagNameNS(this.namespaces[namespace], tagName)
      )
    }

    // Fallback to non-namespaced search
    if (elements.length === 0) {
      elements = Array.from(parent.getElementsByTagName(tagName))
    }

    // Try with namespace prefix
    if (elements.length === 0 && namespace) {
      elements = Array.from(parent.getElementsByTagName(`${namespace}:${tagName}`))
    }

    return elements
  }

  /**
   * Get text content of element by tag name
   * @param {Element} parent - Parent element
   * @param {String} tagName - Tag name
   * @param {String} namespace - Namespace prefix
   * @returns {String} Text content or empty string
   */
  getElementText (parent, tagName, namespace = null) {
    const element = this.getElement(parent, tagName, namespace)
    return this.getTextContent(element)
  }

  /**
   * Get text content from element
   * @param {Element} element - Element
   * @returns {String} Text content or empty string
   */
  getTextContent (element) {
    if (!element) return ''
    return (element.textContent || element.text || '').trim()
  }
}

export default UblXmlParser
